import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient';
import './MyMeetings.css';

/**
 * MyMeetings
 *
 * Visar en lista över alla möten för inloggad användare.
 * Tillåter radering av möte samt navigering till NewMeeting
 * för att återuppta (paused) eller visa möte (completed).
 */
function MyMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchMyMeetings();
  }, []);

  const fetchMyMeetings = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiClient.get('/meetings/my_meetings');
      if (Array.isArray(resp.data)) {
        setMeetings(resp.data);
      } else {
        setError('Felaktigt svar från servern.');
      }
    } catch (err) {
      console.error('Fel vid hämtning av möten:', err);
      setError('Kunde inte hämta möten.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * handleOpenMeeting(meeting)
   * Om status är "paused", navigera till /meeting-content med meeting_id,
   * så att man kan återuppta inspelningen.
   * Om status är "completed", navigera till /meeting-content med meeting_id,
   * men du kan ex. skicka en query-param "completed=1" om du vill öppna mötet direkt.
   */
  const handleOpenMeeting = (meeting) => {
    if (meeting.status === 'paused' || meeting.status === 'in_progress') {
      navigate(`/meeting-content?meeting_id=${meeting.meeting_id}`);
    } else if (meeting.status === 'completed') {
      navigate(`/meeting-content?meeting_id=${meeting.meeting_id}&completed=1`);
    } else {
      alert(`Okänt status: ${meeting.status}`);
    }
  };

  /**
   * handleDeleteMeeting(meeting_id)
   * Klarmarkerar mötet (arkiverar det permanent).
   */
  const handleDeleteMeeting = async (meeting_id) => {
    // Skapa en custom modal istället för window.confirm
    const confirmDelete = () => {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'confirm-modal-overlay';
        modal.innerHTML = `
          <div class="confirm-modal">
            <h3>Klarmarkera möte</h3>
            <p>Vill du klarmarkera detta möte?</p>
            <p class="warning-text">Detta arkiverar mötet permanent och det kan inte återställas.</p>
            <div class="modal-buttons">
              <button class="modal-button cancel-button" onclick="this.closest('.confirm-modal-overlay').remove(); window.resolveConfirm(false)">Avbryt</button>
              <button class="modal-button confirm-button" onclick="this.closest('.confirm-modal-overlay').remove(); window.resolveConfirm(true)">Klarmarkera</button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
        window.resolveConfirm = resolve;
      });
    };
    
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }
    try {
      await apiClient.delete(`/meetings/${meeting_id}`);
      // Ta bort ur listan i state:
      setMeetings((prev) => prev.filter((m) => m.meeting_id !== meeting_id));
    } catch (err) {
      console.error('Fel vid radering av möte:', err);
      alert('Kunde inte radera möte.');
    }
  };

  // Sätt statustext
  const translateStatus = (status) => {
    switch (status) {
      case 'in_progress':
        return 'Pågående';
      case 'paused':
        return 'Pausat';
      case 'completed':
        return 'Avslutat';
      default:
        return status;
    }
  };

  return (
    <div className="my-meetings-container">
      <h2>Mina möten</h2>
      {loading && <p>Laddar...</p>}
      {error && <p className="error-message">{error}</p>}

      {!loading && meetings.length === 0 && (
        <p>Inga möten att visa.</p>
      )}

      {!loading && meetings.length > 0 && (
        <table className="meetings-table">
          <thead>
            <tr>
              <th>Skapad</th>
              <th>Status</th>
              <th>Sammanfattning</th>
              <th>Mötestyp</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {meetings.map((meeting) => (
              <tr key={meeting.meeting_id}>
                <td>
                  {meeting.created_at
                    ? new Date(meeting.created_at).toLocaleString('sv-SE', {
                        timeZone: 'Europe/Stockholm'
                      })
                    : 'Okänd'}
                </td>
                <td>
                  <span className={`status-badge status-${meeting.status}`}>
                    {translateStatus(meeting.status)}
                  </span>
                </td>
                <td>{meeting.summary || meeting.transcription_snippet}</td>
                <td>
                  {meeting.meeting_type ? 
                    String(meeting.meeting_type) : 
                    (meeting.status === 'in_progress' ? 'Pågående' : 
                     meeting.status === 'paused' ? 'Pausat' : 
                     'Ej vald')
                  }
                </td>
                <td className="action-buttons">
                  <button
                    className="meeting-button open-meeting-button"
                    onClick={() => handleOpenMeeting(meeting)}
                  >
                    {meeting.status === 'completed'
                      ? 'Visa möte'
                      : 'Återuppta'}
                  </button>
                  <button
                    className="meeting-button delete-meeting-button"
                    onClick={() => handleDeleteMeeting(meeting.meeting_id)}
                  >
                    Klarmarkera
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default MyMeetings;
